import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Register() {
  const { t } = useLang();
  const [form, setForm] = useState({ first_name: "", last_name: "", birth_date: "", email: "", institution: "", specialization: "" });
  const [privacyOk, setPrivacyOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creds, setCreds] = useState(null);
  const [copied, setCopied] = useState(false);

  const copyPassword = async () => {
    if (!creds?.password) return;
    try {
      await navigator.clipboard.writeText(creds.password);
      setCopied(true);
      toast.success(t("copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(creds.password);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full border border-white/15 bg-ink-900/60 px-4 py-3 font-body text-white outline-none transition-colors focus:border-cyan";
  const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400";

  const submit = async (e) => {
    e.preventDefault();
    if (!privacyOk) {
      toast.error(t("privacy_accept") + " " + t("privacy_policy"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/register", form);
      setCreds({ email: form.email, password: data.password });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="relative flex min-h-screen items-center justify-center px-6 py-28 grid-lines">
      <div className="absolute left-1/2 top-1/3 h-[500px] w-[500px] -translate-x-1/2 glow-blue" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-xl border border-white/10 bg-ink-800/70 p-8 backdrop-blur-xl md:p-10"
      >
        {creds ? (
          <div data-testid="register-success" className="text-center">
            <CheckCircle size={54} weight="thin" className="mx-auto text-cyan" />
            <h1 className="mt-5 font-head text-3xl font-black uppercase tracking-tight text-white">{t("register_success_title")}</h1>
            <p className="mt-4 font-body text-slate-300">{t("register_success_msg")}</p>
            <div data-testid="register-credentials" className="mt-6 border border-cyan/30 bg-ink-900/80 p-5 text-left">
              <div className="flex items-center justify-between gap-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Email</span>
                <span data-testid="register-credential-email" className="font-body text-sm text-white">{creds.email}</span>
              </div>
              <div className="mt-4 flex items-center justify-between gap-4 border-t border-white/10 pt-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{t("your_password")}</span>
                <span className="flex items-center gap-3">
                  <code data-testid="register-generated-password" className="font-mono text-base font-bold tracking-wider text-cyan">{creds.password}</code>
                  <button
                    data-testid="copy-password-button"
                    type="button"
                    onClick={copyPassword}
                    className="flex items-center gap-2 border border-white/20 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white transition-colors hover:border-cyan hover:text-cyan"
                  >
                    <Copy size={14} /> {copied ? t("copied") : t("copy")}
                  </button>
                </span>
              </div>
            </div>
            <p data-testid="register-save-warning" className="mt-5 font-body text-xs leading-relaxed text-amber-400/90">{t("register_save_warning")}</p>
            <Link to="/login" data-testid="go-to-login-button" className="mt-8 inline-block bg-cyan px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink-900 transition-colors hover:bg-white">
              {t("go_to_login")}
            </Link>
          </div>
        ) : (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Wall Board Academy</p>
            <h1 className="mt-3 font-head text-3xl font-black uppercase tracking-tight text-white md:text-4xl">{t("register_title")}</h1>
            <p className="mt-4 font-body text-sm leading-relaxed text-slate-400">{t("register_sub")}</p>
            <form onSubmit={submit} className="mt-8 flex flex-col gap-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>{t("last_name")} *</label>
                  <input data-testid="reg-last-name" required className={inputCls} value={form.last_name} onChange={set("last_name")} />
                </div>
                <div>
                  <label className={labelCls}>{t("first_name")} *</label>
                  <input data-testid="reg-first-name" required className={inputCls} value={form.first_name} onChange={set("first_name")} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>{t("birth_date")} *</label>
                  <input data-testid="reg-birth-date" required type="date" className={`${inputCls} [color-scheme:dark]`} value={form.birth_date} onChange={set("birth_date")} />
                </div>
                <div>
                  <label className={labelCls}>Email *</label>
                  <input data-testid="reg-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} />
                </div>
              </div>
              <div>
                <label className={labelCls}>{t("institution")} <span className="lowercase text-slate-600">({t("optional")})</span></label>
                <input data-testid="reg-institution" className={inputCls} value={form.institution} onChange={set("institution")} />
              </div>
              <div>
                <label className={labelCls}>{t("specialization")} <span className="lowercase text-slate-600">({t("optional")})</span></label>
                <input data-testid="reg-specialization" className={inputCls} value={form.specialization} onChange={set("specialization")} />
              </div>
              <label data-testid="reg-privacy-wrapper" className="mt-2 flex items-start gap-3 cursor-pointer select-none">
                <input
                  data-testid="reg-privacy-checkbox"
                  type="checkbox"
                  required
                  checked={privacyOk}
                  onChange={(e) => setPrivacyOk(e.target.checked)}
                  className="mt-1 h-4 w-4 cursor-pointer accent-cyan"
                />
                <span className="font-body text-sm leading-relaxed text-slate-300">
                  {t("privacy_accept")}{" "}
                  <Link to="/privacy" target="_blank" rel="noreferrer" className="text-cyan underline underline-offset-4 hover:text-white">
                    {t("privacy_policy")}
                  </Link>
                  <span className="text-cyan"> *</span>
                </span>
              </label>
              <button
                data-testid="reg-submit"
                type="submit"
                disabled={loading || !privacyOk}
                className="mt-2 bg-cyan px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink-900 transition-colors hover:bg-white disabled:opacity-50"
              >
                {loading ? "…" : t("submit_request")}
              </button>
            </form>
            <p className="mt-6 font-body text-sm text-slate-500">
              {t("already_account")}{" "}
              <Link to="/login" className="text-cyan hover:text-white">{t("login_here")}</Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
